/**
 * Hooks de fotos de obra: catálogo de obras, galería paginada, asignación de
 * sub-partida y estado de la cola de subida.
 */

import { type FiltroGaleria, cliente } from '@/lib/cliente';
import { EVENTO_FOTOS_SUBIDAS, type EstadoCola, suscribir } from '@/lib/colaFotos';
import type { AsignarFotos, RespuestaApi, TipoCentroCosto } from '@adelante/shared';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

function datos<T>(r: RespuestaApi<T>): T {
  if (r.error !== null) throw new Error(r.error.mensaje);
  return r.data;
}

export function useObrasFotos(q = '') {
  return useQuery({
    queryKey: ['fotos', 'obras', q],
    queryFn: async () => datos(await cliente.fotos.obras(q || undefined)),
    staleTime: 60_000,
  });
}

export function useAutoresFotos() {
  return useQuery({
    queryKey: ['fotos', 'autores'],
    queryFn: async () => datos(await cliente.fotos.autores()),
    staleTime: 5 * 60_000,
  });
}

/** Galería paginada: de una obra (filtro.obra) o general (sin obra). */
export function useGaleria(filtro: FiltroGaleria | null) {
  return useInfiniteQuery({
    queryKey: ['fotos', 'galeria', filtro],
    enabled: filtro !== null,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      datos(await cliente.fotos.galeria({ ...(filtro as FiltroGaleria), antes: pageParam })),
    getNextPageParam: (ultima) => ultima.siguiente,
  });
}

export function useAsignarFotos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AsignarFotos) => datos(await cliente.fotos.asignar(body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fotos'] }),
  });
}

/** Estado de la cola local; refresca la galería cuando se suben fotos. */
export function useColaFotos(): EstadoCola {
  const qc = useQueryClient();
  const [estado, setEstado] = useState<EstadoCola>({ pendientes: 0, conError: 0, subiendo: false });
  useEffect(() => suscribir(setEstado), []);
  useEffect(() => {
    const refrescar = () => qc.invalidateQueries({ queryKey: ['fotos'] });
    window.addEventListener(EVENTO_FOTOS_SUBIDAS, refrescar);
    return () => window.removeEventListener(EVENTO_FOTOS_SUBIDAS, refrescar);
  }, [qc]);
  return estado;
}

// ---------------------------------------------------------------------------
// Configuración de centros de costo (admin)
// ---------------------------------------------------------------------------

export function useConfigFotos() {
  return useQuery({
    queryKey: ['fotos', 'config'],
    queryFn: async () => datos(await cliente.fotos.config()),
  });
}

export function useGuardarConfigFotos() {
  const qc = useQueryClient();
  const refrescar = () => qc.invalidateQueries({ queryKey: ['fotos'] });
  return {
    dias: useMutation({
      mutationFn: async (dias: number) => datos(await cliente.fotos.guardarDias(dias)),
      onSuccess: refrescar,
    }),
    area: useMutation({
      mutationFn: async (b: {
        area_costeo: string;
        tipo: TipoCentroCosto;
        admite_fotos: boolean;
      }) => datos(await cliente.fotos.guardarArea(b)),
      onSuccess: refrescar,
    }),
    obra: useMutation({
      mutationFn: async ({
        codigo,
        fotos_habilitada,
        nota,
      }: { codigo: string; fotos_habilitada: boolean | null; nota?: string | null }) =>
        datos(await cliente.fotos.configurarObra(codigo, { fotos_habilitada, nota })),
      onSuccess: refrescar,
    }),
  };
}
